import { useEffect, useRef } from "react";
import {
  getShortcutAction,
  SHORTCUT_KEYMAP,
  useShipmentTableActions,
} from "@src/common/components/ShipmentsTableDropDown";
import { isTypingTarget } from "@src/common/utils/focusUtils";
import { useShipmentSelectionContext } from "../context/ShipmentSelectionContext";
import { useModalsContext } from "../context/ShipmentsTableModalsContext";

// Bridges row hover and the dropdown's letter shortcuts: while a row is
// hovered (or selected as a fallback), pressing a bound key triggers that
// row's action without having to open the ⋮ menu. Renders nothing.
export function ShipmentRowShortcutListener() {
  const {
    hoveredRow,
    selectedRow,
    setSelectedRow,
    setHoveredRow,
    checkedShipments,
    isActionPaletteOpen,
  } = useShipmentSelectionContext();
  const { openedModalName } = useModalsContext();

  // When a modal or the action palette opens, drop the hovered row. Mouse
  // enter/leave are movement-driven, so opening a modal via keyboard (mouse
  // stationary) never fires onMouseLeave, leaving hoveredRow stale. Without
  // this, the next shortcut after closing would re-target the old row.
  useEffect(() => {
    if (openedModalName || isActionPaletteOpen) setHoveredRow(undefined);
  }, [openedModalName, isActionPaletteOpen, setHoveredRow]);

  const targetRow = hoveredRow ?? selectedRow;
  const { items, isRoleType } = useShipmentTableActions(targetRow, "shipments");

  // Keep the latest values in a ref so the window listener stays subscribed
  // once instead of re-binding on every hover/render.
  const stateRef = useRef({
    items,
    isRoleType,
    targetRow,
    openedModalName,
    checkedShipments,
    isActionPaletteOpen,
    setSelectedRow,
  });
  stateRef.current = {
    items,
    isRoleType,
    targetRow,
    openedModalName,
    checkedShipments,
    isActionPaletteOpen,
    setSelectedRow,
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const {
        items,
        isRoleType,
        targetRow,
        openedModalName,
        checkedShipments,
        isActionPaletteOpen,
        setSelectedRow,
      } = stateRef.current;

      // Let browser/OS shortcuts pass through untouched.
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      // Don't hijack typing in inputs or editable fields. Focused row
      // checkboxes are exempt so clicking one doesn't block shortcuts.
      if (isTypingTarget(document.activeElement)) return;

      // A modal owns the keyboard while open; bulk selection uses the bulk bar.
      if (openedModalName) return;
      // The action palette has its own keyboard handling.
      if (isActionPaletteOpen) return;
      if (checkedShipments.length > 0) return;
      if (!targetRow) return;

      if (!SHORTCUT_KEYMAP[event.key.toLowerCase()]) return;
      const action = getShortcutAction(items, event.key);
      if (!action || !isRoleType(action.minimumUserRoleType)) return;
      if (action.disabled && action.disabled(targetRow)) return;

      event.preventDefault();
      // Mirror the dropdown's behavior of selecting the row before acting, so
      // actions that read selectedRow (e.g. Assign Driver) target this row.
      setSelectedRow(targetRow);
      action.onClick(targetRow);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  return null;
}

export default ShipmentRowShortcutListener;
